import { useState, useEffect } from 'react';

/* Which of the given section ids the reader is currently in.
 *
 * Deliberately scroll math rather than an IntersectionObserver. The rule we
 * want — "the last section whose top has passed under the header" — is one
 * comparison, and expressing it directly means the answer is the same whether
 * sections are contiguous or not. "Other Noteworthy Projects" carries no id of
 * its own, so an observer watching only the listed sections reports nothing
 * while you scroll through it; this keeps Projects lit, which is what a reader
 * would say they were looking at.
 *
 * Above the first section is the hero. It has no nav link, so nothing is lit.
 */
const useActiveSection = (ids, enabled = true) => {
  const [activeId, setActiveId] = useState(null);

  // Depend on the contents, not the array identity — a fresh literal on every
  // render would tear the listeners down and rebuild them each time.
  const key = ids.join(',');

  useEffect(() => {
    if (!enabled) {
      setActiveId(null);
      return undefined;
    }

    let queued = false;

    const measure = () => {
      queued = false;

      const elements = key
        .split(',')
        .map(id => document.getElementById(id))
        .filter(Boolean);

      if (elements.length === 0) {
        return;
      }

      const navHeight =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--nav-scroll-height'),
          10,
        ) || 0;

      // A line a little below the header. Not the header's own edge: a section
      // would then take over the moment its very first pixel appeared, which
      // reads as the highlight running ahead of the page.
      const line = navHeight + window.innerHeight * 0.25;

      let current = null;
      elements.forEach(el => {
        if (el.getBoundingClientRect().top <= line) {
          current = el.id;
        }
      });

      /* The last section can never win on the rule above: once you reach the
         bottom of the document there is no scroll left for its top to travel
         the remaining distance to the line, so it stays unlit no matter how
         long you look at it. Someone at the end of the page is looking at the
         last section, so award it outright. Guarded on the page being taller
         than the viewport, since otherwise "at the bottom" is true on load. */
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight > window.innerHeight + 4;
      const atBottom = window.innerHeight + window.pageYOffset >= doc.scrollHeight - 2;
      if (scrollable && atBottom) {
        current = elements[elements.length - 1].id;
      }

      setActiveId(current);
    };

    const onScroll = () => {
      if (!queued) {
        queued = true;
        window.requestAnimationFrame(measure);
      }
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [enabled, key]);

  return activeId;
};

export default useActiveSection;
